import { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Ordered permission levels: None < View < Edit. Mirrors the backend authz
// ordering (lambda/.../authz.py LEVELS) so the convenience-only UI gating
// matches the authoritative server-side enforcement.
export const LEVELS = ['None', 'View', 'Edit'];

// Controllable tabs that a Superadmin can grant to a Staff user. Matches the
// design glossary "Controllable Tab Set".
export const CONTROLLABLE_TABS = [
  'Products',
  'Categories',
  'Locations',
  'Orders/Reports',
  'POS Queue',
  // Staff order entry, split out of 'POS Queue' by the order-additions spec so a
  // waiter needs no POS-configuration rights. Granted independently: there is NO
  // inheritance from 'POS Queue'.
  //
  // Must stay in step with `TABS` in lambda/common/authz.py and with
  // CONTROLLABLE_TABS in admin-mobile-app/src/permissions/tabGating.js. The
  // backend REJECTS a permission map naming a tab it does not know
  // (validation.validate_tab -> INVALID_TAB), so an entry present here but absent
  // there makes every Staff permission save fail.
  'Take Order',
  'Discounts',
  'Reviews',
  'Audit',
  'Theme',
  'General Settings',
  'Otter',
];

/**
 * levelAtLeast(granted, required) -> boolean
 *
 * Returns true when the granted permission level is at least the required one
 * under the ordering None < View < Edit. Fails closed (returns false) for any
 * unrecognized level so an unexpected value never widens access.
 */
export function levelAtLeast(granted, required) {
  const g = LEVELS.indexOf(granted);
  const r = LEVELS.indexOf(required);
  if (g < 0 || r < 0) return false;
  return g >= r;
}

// Module-level cache so the capability is fetched once and shared across the
// many pages that each render <AdminNavTabs/>, instead of re-fetching on every
// navigation. Only successful responses are cached; failures are left uncached
// so a later page mount can retry (and we always fail closed in the meantime).
let _capabilityCache = null; // { username, role, permissions, isSuperadmin }
let _cachedToken = null; // the idToken the cache belongs to (per-user invalidation)
let _inflight = null;

// Clears the shared capability cache. Called when the login page mounts so a
// new sign-in never inherits the previous user's cached role/permissions.
export function resetCapabilityCache() {
  _capabilityCache = null;
  _cachedToken = null;
  _inflight = null;
}

async function fetchCapability() {
  if (_inflight) return _inflight;

  _inflight = (async () => {
    try {
      if (!API_BASE_URL) throw new Error('admin api base url not configured');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      // Serve the cache ONLY when it belongs to the current user's token; a
      // different user (or refreshed token) forces a refetch so capabilities
      // never leak across sign-ins.
      if (_capabilityCache && _cachedToken === token) {
        return { data: _capabilityCache, error: null };
      }
      const res = await fetch(`${API_BASE_URL}/admin/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`/admin/users/me responded ${res.status}`);
      const data = await res.json();
      _capabilityCache = {
        username: data?.username ?? null,
        role: data?.role ?? null,
        permissions: data?.permissions && typeof data.permissions === 'object' ? data.permissions : {},
        isSuperadmin: !!data?.isSuperadmin,
      };
      _cachedToken = token;
      return { data: _capabilityCache, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      _inflight = null;
    }
  })();

  return _inflight;
}

/**
 * useAdminCapability()
 *
 * Fetches GET /admin/users/me once (shared cache) and exposes the caller's
 * capability for UI gating. Fails closed: while loading or on error, `loaded`
 * is false, `isSuperadmin` is false, and callers should fall back to the
 * existing Cognito-group-based behavior so existing Admins keep access.
 *
 * Returned shape:
 *   { loading, loaded, error, username, role, permissions, isSuperadmin,
 *     isAdminRole, canView(tab), canEdit(tab),
 *     canViewTab(tab), canEditTab(tab), levelAtLeast }
 *
 * IMPORTANT — WHICH PAIR TO USE:
 *
 *   `canView` / `canEdit` consult ONLY the per-tab permission map. That map is
 *   `{}` for Admin and Superadmin, because their access is role-based rather
 *   than per-tab (see handle_me in admin-user-mgmt-handler: "permissions is the
 *   per-tab grant map for Staff and {} for Admin/Superadmin"). So `canEdit(tab)`
 *   is ALWAYS FALSE for an Admin or Superadmin — correct for "does this Staff
 *   grant exist", wrong for "may this caller do X", and a trap that has already
 *   hidden two admin-visible controls.
 *
 *   `canViewTab` / `canEditTab` are the ones to gate UI on: they return true for
 *   Admin/Superadmin by role and otherwise fall through to the per-tab grant.
 *
 * Either way this is convenience gating only; the server enforces the real
 * check independently via authz.ROUTE_PERMISSIONS.
 */
export function useAdminCapability() {
  const [state, setState] = useState(() => ({
    loading: !_capabilityCache,
    loaded: !!_capabilityCache,
    error: null,
    username: _capabilityCache?.username ?? null,
    role: _capabilityCache?.role ?? null,
    permissions: _capabilityCache?.permissions ?? {},
    isSuperadmin: !!_capabilityCache?.isSuperadmin,
  }));

  useEffect(() => {
    let mounted = true;
    fetchCapability().then(({ data, error }) => {
      if (!mounted) return;
      if (error || !data) {
        // Fail closed: no User Management, no Staff grants. Callers fall back
        // to the group-based behavior.
        setState({
          loading: false,
          loaded: false,
          error: error || new Error('no capability data'),
          username: null,
          role: null,
          permissions: {},
          isSuperadmin: false,
        });
        return;
      }
      setState({
        loading: false,
        loaded: true,
        error: null,
        username: data.username,
        role: data.role,
        permissions: data.permissions,
        isSuperadmin: data.isSuperadmin,
      });
    });
    return () => {
      mounted = false;
    };
  }, []);

  const canView = (tab) => levelAtLeast(state.permissions?.[tab], 'View');
  const canEdit = (tab) => levelAtLeast(state.permissions?.[tab], 'Edit');

  // Admin and Superadmin bypass the per-tab matrix entirely, exactly as
  // authz.authorize() does server-side. Fails closed while loading or on error,
  // since `role` is null until the capability resolves.
  const isAdminRole = state.role === 'Admin' || state.role === 'Superadmin';
  const canViewTab = (tab) => isAdminRole || canView(tab);
  const canEditTab = (tab) => isAdminRole || canEdit(tab);

  return { ...state, isAdminRole, canView, canEdit, canViewTab, canEditTab, levelAtLeast };
}
